import React, { useState, useEffect } from 'react';
import { Send, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { HeadToHeadModal } from './HeadToHeadModal';
import './MatchFeed.css';

type MatchStatus = 'SCHEDULED' | 'PENDING' | 'AWAITING_APPROVAL' | 'COMPLETED' | 'WAITING';

export interface MatchFeedCardProps {
  matchId: string;
  status: MatchStatus;
  player1: { id: string; name: string; photoUrl?: string };
  player2?: { id: string; name: string; photoUrl?: string };
  date?: string;
  time?: string;
  location?: string;
  court?: string;
  score?: string;
  winnerId?: string;
  competitionName?: string;
  stage?: string;
  category?: string;
  onDelete?: (matchId: string) => void;
  onPlayerClick?: (playerId: string) => void;
  displayMatchNumber?: number;
  pointsConfig?: any;
}

const COMMENT_PILLS = ["Acaba logo, saibro!", "Vou querer revanche!", "Esse placar não diz o que foi o jogo"];

export const MatchFeedCard: React.FC<MatchFeedCardProps> = ({
  matchId,
  status,
  player1,
  player2,
  date,
  time,
  location,
  court,
  score,
  winnerId,
  competitionName,
  stage,
  category,
  onPlayerClick,
  displayMatchNumber,
  pointsConfig
}) => {
  const [superLikes, setSuperLikes] = useState(0);
  const [fairPlayCount, setFairPlayCount] = useState(0);
  const [winnerPrestige, setWinnerPrestige] = useState(0);
  const [respectCount, setRespectCount] = useState(0);
  const [dominationCount, setDominationCount] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);
  const [predictions, setPredictions] = useState<{ total: number, options: { label: string, count: number }[] }>({ total: 0, options: [] });
  
  const [showPredictionModal, setShowPredictionModal] = useState(false);
  const [predictionSubMenu, setPredictionSubMenu] = useState<'2x0' | '6/4' | null>(null);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [showH2HModal, setShowH2HModal] = useState(false);
  
  const [showInteractionsModal, setShowInteractionsModal] = useState(false);
  const [interactionsModalTitle, setInteractionsModalTitle] = useState('');
  const [interactionsModalUsers, setInteractionsModalUsers] = useState<{id: string, name: string}[]>([]);
  
  const [interactionsData, setInteractionsData] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{id: string, name: string} | null>(null);
  const [myInteractions, setMyInteractions] = useState<string[]>([]);

  const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";
  const token = localStorage.getItem('tt_auth_token');

  useEffect(() => {
    fetchInteractions();
  }, [matchId]);

  const fetchInteractions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/competitions/matches/${matchId}/interactions/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data) {
        setSuperLikes(data.SUPER_LIKE || 0);
        setFairPlayCount(data.FAIR_PLAY || 0);
        setWinnerPrestige(data.WINNER_PRESTIGE || 0);
        setRespectCount(data.RESPECT || 0);
        setDominationCount(data.DOMINATION || 0);
        setCommentsCount(data.commentsCount || 0);
        setPredictions(data.predictions || { total: 0, options: [] });
        setInteractionsData(data);
        if (data.myInteractions) setMyInteractions(data.myInteractions);
      }
    } catch (e) {
      console.log('Error fetching interactions', e);
    }
  };

  const fetchComments = async () => {
    try {
      setLoadingComments(true);
      const response = await fetch(`${API_URL}/api/competitions/matches/${matchId}/comments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (e) {
      console.log('Error fetching comments', e);
    } finally {
      setLoadingComments(false);
    }
  };

  const openComments = () => {
    setShowCommentsModal(true);
    fetchComments();
  };

  const handleInteract = async (type: string, value?: string) => {
    const isUntoggling = myInteractions.includes(type) && (type !== 'PREDICTION');
    
    if (type === 'SUPER_LIKE') setSuperLikes(prev => isUntoggling ? Math.max(0, prev - 1) : prev + 1);
    if (type === 'FAIR_PLAY') setFairPlayCount(prev => isUntoggling ? Math.max(0, prev - 1) : prev + 1);
    if (type === 'WINNER_PRESTIGE') setWinnerPrestige(prev => isUntoggling ? Math.max(0, prev - 1) : prev + 1);
    if (type === 'RESPECT') setRespectCount(prev => isUntoggling ? Math.max(0, prev - 1) : prev + 1);
    if (type === 'DOMINATION') setDominationCount(prev => isUntoggling ? Math.max(0, prev - 1) : prev + 1);

    if (type !== 'PREDICTION') {
      setMyInteractions(prev => isUntoggling ? prev.filter(t => t !== type) : [...prev, type]);
    }

    try {
      await fetch(`${API_URL}/api/competitions/matches/${matchId}/interact`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ type, value })
      });
      if (type === 'PREDICTION') {
        setShowPredictionModal(false);
        fetchInteractions();
      }
    } catch (e) {
      console.log('Error interacting', e);
    }
  };

  const handlePostComment = async (text: string, isPill: boolean = false) => {
    if (!text.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/competitions/matches/${matchId}/comments`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text, isPill, parentId: replyingTo?.id })
      });
      if (response.ok) {
        setNewComment('');
        setReplyingTo(null);
        fetchComments();
      }
    } catch (e) {
      console.log('Error posting comment', e);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    try {
      const res = await fetch(`${API_URL}/api/competitions/matches/comments/${commentId}/like`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setComments(prev => prev.map(c => 
          c.id === commentId ? { ...c, likedByMe: data.liked, likesCount: (c.likesCount || 0) + (data.liked ? 1 : -1) } : c
        ));
      }
    } catch (e) {
      console.error('Error liking comment', e);
    }
  };

  const isHighlighted = superLikes >= 5;
  const checkDomination = (scoreStr?: string) => {
    if (!scoreStr) return false;
    const sets = scoreStr.trim().split(/\s+/);
    if (sets.length !== 2) return false;

    let hasOneOrZero = false;
    let allSetsUnderFour = true;

    for (const set of sets) {
      const games = set.split('/').map(n => parseInt(n, 10));
      if (games.length !== 2 || isNaN(games[0]) || isNaN(games[1])) return false;
      const loserGames = Math.min(games[0], games[1]);
      
      if (loserGames <= 1) hasOneOrZero = true;
      if (loserGames > 3) allSetsUnderFour = false;
    }

    return hasOneOrZero && allSetsUnderFour;
  };

  const isDomination = checkDomination(score);

  const getInitials = (name?: string) => {
    if (!name || name === '?') return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const renderPlayer = (player?: { id: string; name: string; photoUrl?: string }, isWinner?: boolean) => {
    if (!player) return (
      <div className="mf-player">
        <div className="mf-avatar">?</div>
        <div className="mf-player-name" style={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>A definir</div>
      </div>
    );
    const isCompleted = status === 'COMPLETED';
    let ptsWon = 0;
    if (isCompleted && pointsConfig) {
      if (score?.includes('W.O.')) {
        ptsWon = isWinner ? (pointsConfig.woWinner ?? 10) : (pointsConfig.woLoser ?? -5);
      } else {
        ptsWon = isWinner ? (pointsConfig.winner ?? 10) : (pointsConfig.loser ?? 2);
      }
    }

    return (
      <div 
        className="mf-player" 
        onClick={() => onPlayerClick && onPlayerClick(player.id)}
        style={{ cursor: onPlayerClick ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <div className={`mf-avatar ${isWinner ? 'winner' : ''}`}>
          {player.photoUrl ? (
            <img src={player.photoUrl} alt={player.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            getInitials(player.name)
          )}
        </div>
        <div className={`mf-player-name ${isWinner ? 'winner' : ''}`} style={{ textAlign: 'center' }}>
          {player.name}
        </div>
        {isCompleted && (
          <div style={{ fontSize: '12px', marginTop: '4px', fontWeight: 'bold', color: isWinner ? 'var(--primary-color)' : 'rgba(255,255,255,0.4)' }}>
            {ptsWon > 0 ? `+${ptsWon} pts` : `${ptsWon} pts`}
          </div>
        )}
      </div>
    );
  };

  const isCompletedOrAwaiting = status === 'COMPLETED' || status === 'AWAITING_APPROVAL';
  const shouldSwapPlayers = isCompletedOrAwaiting && winnerId && player2 && winnerId === player2.id;
  
  const displayPlayer1 = shouldSwapPlayers ? player2 : player1;
  const displayPlayer2 = shouldSwapPlayers ? player1 : player2;
  
  let displayScore = score;
  if (shouldSwapPlayers && score) {
    displayScore = score.split(' ').map((s: string) => {
      if (s.includes('W.O.')) return s;
      const match = s.match(/^(\d+)\/(\d+)(?:\((\d+)(?:-(\d+))?\))?$/);
      if (match) {
        let tb = '';
        if (match[3] && match[4]) {
          tb = `(${match[4]}-${match[3]})`;
        } else if (match[3]) {
          tb = `(${match[3]})`;
        }
        return `${match[2]}/${match[1]}${tb}`;
      }
      return s;
    }).join(' ');
  }

  // Auto-correct the score if it appears to be reversed for the winner
  if (isCompletedOrAwaiting && winnerId && displayScore) {
    let p1SetsWon = 0;
    let p2SetsWon = 0;
    
    const sets = displayScore.split(' ');
    sets.forEach((s: string) => {
      const match = s.match(/^(\d+)\/(\d+)(?:\((\d+)(?:-(\d+))?\))?$/);
      if (match) {
        const games1 = parseInt(match[1], 10);
        const games2 = parseInt(match[2], 10);
        if (games1 > games2) p1SetsWon++;
        else if (games2 > games1) p2SetsWon++;
      } else if (s.includes('W.O.')) {
        const woMatch = s.match(/^(\d+)\/(\d+)/);
        if (woMatch) {
           const g1 = parseInt(woMatch[1], 10);
           const g2 = parseInt(woMatch[2], 10);
           if (g1 > g2) p1SetsWon++;
           else if (g2 > g1) p2SetsWon++;
        }
      }
    });

    // If the left side (winner) has fewer sets won than the right side, flip the score
    if (p2SetsWon > p1SetsWon) {
      displayScore = displayScore.split(' ').map((s: string) => {
        if (s.includes('W.O.')) {
          const woMatch = s.match(/^(\d+)\/(\d+)/);
          if (woMatch) return s.replace(`${woMatch[1]}/${woMatch[2]}`, `${woMatch[2]}/${woMatch[1]}`);
          return s;
        }
        const match = s.match(/^(\d+)\/(\d+)(?:\((\d+)(?:-(\d+))?\))?$/);
        if (match) {
          let tb = '';
          if (match[3] && match[4]) {
            tb = `(${match[4]}-${match[3]})`;
          } else if (match[3]) {
            tb = `(${match[3]})`;
          }
          return `${match[2]}/${match[1]}${tb}`;
        }
        return s;
      }).join(' ');
    }
  }

  return (
    <div className={`mf-card ${isHighlighted ? 'highlighted' : ''}`}>
      {isHighlighted && <div className="mf-highlight-tag">⭐ Destaque da Rodada</div>}
      
      <div className="mf-header" style={{flexDirection: 'column', alignItems: 'flex-start', gap: '8px'}}>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '12px', color: 'var(--primary-color, #caff04)', letterSpacing: '0.5px' }}>
              {(competitionName || 'Amistoso').toUpperCase().replace(/(\d)O\b/g, '$1º')}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {category && (
                <div style={{ padding: '4px 8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', color: 'white', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                  {category}
                </div>
              )}
              {stage && (
                <div style={{ padding: '4px 8px', background: 'rgba(202, 255, 4, 0.1)', borderRadius: '4px', color: 'var(--primary-color, #caff04)', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                  {stage}
                </div>
              )}
              {displayMatchNumber && (
                <div style={{ padding: '4px 8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.8)', fontSize: '11px', fontWeight: 'bold' }}>
                  Jogo {displayMatchNumber}
                </div>
              )}
            </div>
          </div>
          <div className={`mf-status ${status.toLowerCase()}`}>
            {status === 'COMPLETED' ? 'Concluído' : status === 'PENDING' ? 'Aguardando resultado' : status === 'WAITING' ? 'Aguardando Jogadores' : status === 'AWAITING_APPROVAL' ? 'A confirmar' : 'Agendado'}
          </div>
        </div>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="mf-date">
            {date ? new Date(date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'Data a definir'} 
            {time ? ` às ${time}` : ''}
          </span>
        </div>
        {(location || court) && (
          <div style={{display: 'flex', gap: '12px', color: 'rgba(255,255,255,0.6)', fontSize: '12px', flexWrap: 'wrap'}}>
            {location && <span>📍 {location}</span>}
            {court && <span>🎾 {court}</span>}
          </div>
        )}
      </div>

      <div className="mf-players-row">
        {renderPlayer(displayPlayer1, isCompletedOrAwaiting && winnerId === displayPlayer1?.id)}
        
        <div className="mf-vs-container" style={{ position: 'relative' }}>
          {status === 'COMPLETED' && displayScore ? (
            <span className="mf-score-text">{displayScore}</span>
          ) : (
            <span className="mf-vs-text">VS</span>
          )}
          {player1 && player2 && (
            <button 
              onClick={(e) => { e.stopPropagation(); setShowH2HModal(true); }}
              style={{ position: 'absolute', bottom: '-24px', fontSize: '10px', background: '#3A8E58', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontWeight: 'bold' }}>
              H2H
            </button>
          )}
        </div>

        {renderPlayer(displayPlayer2, isCompletedOrAwaiting && !!displayPlayer2 && winnerId === displayPlayer2.id)}
      </div>

      <div className="mf-actions-row">
        {status === 'SCHEDULED' || status === 'PENDING' || status === 'AWAITING_APPROVAL' || status === 'WAITING' ? (
          <>
            <div className="mf-action-btn" title="Imperdível" style={{ opacity: myInteractions.includes('SUPER_LIKE') ? 1 : 0.7 }}>
              <span className="mf-action-icon" onClick={() => handleInteract('SUPER_LIKE')} style={{cursor: 'pointer'}}>🔥</span>
              <button onClick={() => superLikes > 0 ? (setInteractionsModalTitle('🔥 Imperdível'), setInteractionsModalUsers(interactionsData?.superLikeUsers || []), setShowInteractionsModal(true)) : handleInteract('SUPER_LIKE')} title="Imperdível" className="flex items-center space-x-1" style={{padding: '4px 0', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit'}}>
                {superLikes > 0 ? superLikes : 'Imperdível'}
              </button>
            </div>
            
            <div className="mf-action-btn" title="Na Mosca" style={{ opacity: myInteractions.includes('PREDICTION') ? 1 : 0.7 }}>
              <span className="mf-action-icon" onClick={() => { setPredictionSubMenu(null); setShowPredictionModal(true); }} style={{cursor: 'pointer'}}>🎯</span>
              <button onClick={() => predictions.total > 0 ? (setInteractionsModalTitle('🎯 Na Mosca'), setInteractionsModalUsers(interactionsData?.predictionsUsers || []), setShowInteractionsModal(true)) : (setPredictionSubMenu(null), setShowPredictionModal(true))} title="Na Mosca" className="flex items-center space-x-1" style={{padding: '4px 0', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit'}}>
                Na Mosca {predictions.total > 0 ? `(${predictions.total})` : ''}
              </button>
            </div>

            <div className="mf-action-btn" title="Jogo de Cavalheiros" style={{ opacity: myInteractions.includes('FAIR_PLAY') ? 1 : 0.7 }}>
              <span className="mf-action-icon" onClick={() => handleInteract('FAIR_PLAY')} style={{cursor: 'pointer'}}>🤝</span>
              <button onClick={() => fairPlayCount > 0 ? (setInteractionsModalTitle('🤝 Jogo de Cavalheiros'), setInteractionsModalUsers(interactionsData?.fairPlayUsers || []), setShowInteractionsModal(true)) : handleInteract('FAIR_PLAY')} title="Jogo de Cavalheiros" className="flex items-center space-x-1" style={{padding: '4px 0', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit'}}>
                {fairPlayCount > 0 ? fairPlayCount : 'Jogo de Cavalheiros'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mf-action-btn" title="Winner!" style={{ opacity: myInteractions.includes('WINNER_PRESTIGE') ? 1 : 0.7 }}>
              <span className="mf-action-icon" onClick={() => handleInteract('WINNER_PRESTIGE')} style={{cursor: 'pointer'}}>🚀</span>
              <button onClick={() => winnerPrestige > 0 ? (setInteractionsModalTitle('🚀 Winner!'), setInteractionsModalUsers(interactionsData?.winnerPrestigeUsers || []), setShowInteractionsModal(true)) : handleInteract('WINNER_PRESTIGE')} title="Winner!" className="flex items-center space-x-1" style={{padding: '4px 0', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit'}}>
                {winnerPrestige > 0 ? winnerPrestige : 'Winner!'}
              </button>
            </div>

            <div className="mf-action-btn" title="Casca Grossa" style={{ opacity: myInteractions.includes('RESPECT') ? 1 : 0.7 }}>
              <span className="mf-action-icon" onClick={() => handleInteract('RESPECT')} style={{cursor: 'pointer'}}>🩹</span>
              <button onClick={() => respectCount > 0 ? (setInteractionsModalTitle('🩹 Casca Grossa'), setInteractionsModalUsers(interactionsData?.respectUsers || []), setShowInteractionsModal(true)) : handleInteract('RESPECT')} title="Casca Grossa" className="flex items-center space-x-1" style={{padding: '4px 0', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit'}}>
                {respectCount > 0 ? respectCount : 'Casca Grossa'}
              </button>
            </div>

            {isDomination && (
              <div className="mf-action-btn" title="Trator" style={{ opacity: myInteractions.includes('DOMINATION') ? 1 : 0.7 }}>
                <span className="mf-action-icon" onClick={() => handleInteract('DOMINATION')} style={{cursor: 'pointer'}}>🚜</span>
                <button onClick={() => dominationCount > 0 ? (setInteractionsModalTitle('🚜 Trator'), setInteractionsModalUsers(interactionsData?.dominationUsers || []), setShowInteractionsModal(true)) : handleInteract('DOMINATION')} title="Trator" className="flex items-center space-x-1" style={{padding: '4px 0', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit'}}>
                  {dominationCount > 0 ? dominationCount : 'Trator'}
                </button>
              </div>
            )}
          </>
        )}
        
        <div className="mf-action-btn" title="Resenha">
          <span className="mf-action-icon" onClick={openComments} style={{cursor: 'pointer'}}>💬</span>
          <span onClick={openComments} style={{cursor: 'pointer', paddingLeft: '4px'}}>
            {commentsCount > 0 ? `Resenha (${commentsCount})` : 'Resenha'}
          </span>
        </div>
      </div>

      {(status === 'SCHEDULED' || status === 'PENDING' || status === 'AWAITING_APPROVAL' || status === 'WAITING') && predictions.total > 0 && (
        <div className="mf-predictions-footer">
          {predictions.options.sort((a,b) => b.count - a.count).slice(0, 1).map((opt, i) => (
            <span key={i}>
              {Math.round((opt.count / predictions.total) * 100)}% cravam: <strong>{opt.label}</strong>
            </span>
          ))}
        </div>
      )}

      {/* Prediction Modal */}
      {showPredictionModal && createPortal(
        <div className="mf-modal-overlay" onClick={() => setShowPredictionModal(false)}>
          <div className="mf-modal-content" onClick={e => e.stopPropagation()}>
            <div className="mf-modal-header">
              <div>
                <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>🎯 Na Mosca!</h3>
                <span style={{fontSize: '14px', color: 'rgba(255,255,255,0.6)'}}>
                  {predictionSubMenu ? 'Para qual jogador?' : 'Como vai ser esse jogo?'}
                </span>
              </div>
              <button className="mf-close-btn" onClick={() => setShowPredictionModal(false)}><X size={20} /></button>
            </div>
            
            {!predictionSubMenu ? (
              <>
                <button className="mf-pill-btn" onClick={() => setPredictionSubMenu('2x0')}>
                  2x0 Liso
                </button>
                <button className="mf-pill-btn" onClick={() => handleInteract('PREDICTION', 'Vai pro Supertiebreak')}>
                  Vai pro Supertiebreak
                </button>
                <button className="mf-pill-btn" onClick={() => setPredictionSubMenu('6/4')}>
                  Duplo 6/4
                </button>
              </>
            ) : (
              <>
                <button className="mf-pill-btn" onClick={() => handleInteract('PREDICTION', `${predictionSubMenu === '2x0' ? '2x0 Liso' : 'Duplo 6/4'} para ${player1?.name || 'Jogador 1'}`)}>
                  {predictionSubMenu === '2x0' ? '2x0 Liso' : 'Duplo 6/4'} para {player1?.name || 'Jogador 1'}
                </button>
                {player2 && (
                  <button className="mf-pill-btn" onClick={() => handleInteract('PREDICTION', `${predictionSubMenu === '2x0' ? '2x0 Liso' : 'Duplo 6/4'} para ${player2?.name || 'Jogador 2'}`)}>
                    {predictionSubMenu === '2x0' ? '2x0 Liso' : 'Duplo 6/4'} para {player2?.name || 'Jogador 2'}
                  </button>
                )}
                <button className="mf-pill-btn" style={{marginTop: '12px', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)'}} onClick={() => setPredictionSubMenu(null)}>
                  Voltar
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Comments Modal */}
      {showCommentsModal && createPortal(
        <div className="mf-modal-overlay" onClick={() => setShowCommentsModal(false)}>
          <div className="mf-modal-content" onClick={e => e.stopPropagation()}>
            <div className="mf-modal-header">
              <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>💬 Resenha</h3>
              <button className="mf-close-btn" onClick={() => setShowCommentsModal(false)}><X size={20} /></button>
            </div>
            
            <div className="mf-comments-list">
              {loadingComments ? (
                <div style={{textAlign: 'center', padding: '20px', color: 'var(--primary-color)'}}>Carregando...</div>
              ) : comments.length === 0 ? (
                <div style={{textAlign: 'center', padding: '40px 20px', color: 'rgba(255,255,255,0.4)'}}>Seja o primeiro a comentar!</div>
              ) : (
                comments.filter((c: any) => !c.parentId).map((item: any) => (
                  <div key={item.id} className="mf-comment-item">
                    <div className="mf-comment-author">{item.user?.name || 'Usuário'}</div>
                    <div className="mf-comment-text">{item.text}</div>
                    <div className="mf-comment-actions" style={{display: 'flex', gap: '12px', fontSize: '12px', marginTop: '4px'}}>
                      <span onClick={() => handleLikeComment(item.id)} style={{cursor: 'pointer', color: item.likedByMe ? '#ff4d4f' : '#888'}}>
                        {item.likedByMe ? '❤️' : '🤍'} {item.likesCount || 0}
                      </span>
                      <span onClick={() => setReplyingTo({id: item.id, name: item.user?.name})} style={{cursor: 'pointer', color: '#888'}}>
                        Responder
                      </span>
                    </div>

                    {comments.filter((r: any) => r.parentId === item.id).map((reply: any) => (
                      <div key={reply.id} className="mf-comment-item mf-reply" style={{marginLeft: '20px', marginTop: '10px', borderLeft: '2px solid rgba(255,255,255,0.1)', paddingLeft: '10px'}}>
                        <div className="mf-comment-author">{reply.user?.name || 'Usuário'}</div>
                        <div className="mf-comment-text">{reply.text}</div>
                        <div className="mf-comment-actions" style={{display: 'flex', gap: '12px', fontSize: '12px', marginTop: '4px'}}>
                          <span onClick={() => handleLikeComment(reply.id)} style={{cursor: 'pointer', color: reply.likedByMe ? '#ff4d4f' : '#888'}}>
                            {reply.likedByMe ? '❤️' : '🤍'} {reply.likesCount || 0}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            <div className="mf-comment-input-area">
              {!replyingTo && (
                <div className="mf-quick-pills">
                  {COMMENT_PILLS.map(item => (
                    <div key={item} className="mf-quick-pill" onClick={() => handlePostComment(item, true)}>
                      {item}
                    </div>
                  ))}
                </div>
              )}
              
              {replyingTo && (
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '12px', color: '#888', background: 'rgba(255,255,255,0.05)', padding: '4px 8px', borderRadius: '4px'}}>
                  <span>Respondendo a <strong>{replyingTo.name}</strong></span>
                  <button style={{background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: '0'}} onClick={() => setReplyingTo(null)}><X size={14}/></button>
                </div>
              )}
              
              <div className="mf-input-row">
                <input 
                  type="text" 
                  className="mf-text-input" 
                  placeholder={replyingTo ? "Escreva sua resposta..." : "Escreva sua resenha..."}
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handlePostComment(newComment, false)}
                />
                <button className="mf-send-btn" onClick={() => handlePostComment(newComment, false)}>
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Interactions List Modal */}
      {showInteractionsModal && createPortal(
        <div className="mf-modal-overlay" onClick={() => setShowInteractionsModal(false)}>
          <div className="mf-modal-content" onClick={e => e.stopPropagation()}>
            <div className="mf-modal-header">
              <h3 style={{display: 'flex', alignItems: 'center', gap: '8px'}}>{interactionsModalTitle}</h3>
              <button className="mf-close-btn" onClick={() => setShowInteractionsModal(false)}><X size={20} /></button>
            </div>
            
            <div className="mf-comments-list" style={{minHeight: '100px'}}>
              {interactionsModalUsers.length === 0 ? (
                <div style={{textAlign: 'center', color: 'rgba(255,255,255,0.5)', marginTop: '20px'}}>Ninguém interagiu ainda.</div>
              ) : (
                interactionsModalUsers.map((u, idx) => (
                  <div key={idx} style={{padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <div style={{width: '32px', height: '32px', borderRadius: '16px', backgroundColor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                      {(u.name || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{fontWeight: 'bold'}}>{u.name}</div>
                      {(u as any).value && <div style={{fontSize: '12px', color: 'var(--primary-color)'}}>{(u as any).value}</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {showH2HModal && player1 && player2 && (
        <HeadToHeadModal
          player1Id={player1.id}
          player2Id={player2.id}
          player1Name={player1.name}
          player2Name={player2.name}
          onClose={() => setShowH2HModal(false)}
        />
      )}
    </div>
  );
};
